/**
 * Whether a postcode is one Grow can plan a garden from, as the backend
 * decides it (routes/postcodes.js, services/postcodeService.js): a real US
 * ZIP that Grow has a climate profile and a weather station for. Asked as a
 * box is filled in, to say the place ("Sparta, TN") before it is saved; the
 * save checks again and refuses one that is not. A postcode outside the US
 * can be left with an email, to be told when Grow reaches it (askToBeTold).
 *
 * Endpoints:
 * - GET  /postcodes/:code → { success, ...PostcodeCheck } (refusals are
 *   answers, ok: false, not errors).
 * - POST /postcodes/notify WaitlistRequest.
 * Saving a postcode is the profile's (api/users updateUserProfile), which
 * refuses with a POSTCODE_* error that postcodeRefusal reads.
 *
 * Who uses it: features/today TodayPostcode and the profile's
 * ProfileDetailsForm (the check, as a box is filled in); whether the saved
 * one can be planned from is the garden place's answer (api/gardenPlace),
 * whose refusal words shared/hooks/useGardenPostcode takes from
 * postcodeRefusalWords. Caching:
 * postcodeCheckKey(zip) (['postcode-check', zip]) with staleTime Infinity:
 * a ZIP's answer does not change within a visit.
 *
 * @module api/postcodes
 */

import { apiClient } from './apiClient';
import { t } from '../i18n/i18nSetup';

/** The check's answer: the place, or why not with words to show. */
export type PostcodeCheck =
  | { ok: true; zip: string; country: string; city: string | null; state: string | null; latitude: number; longitude: number }
  | { ok: false; reason: 'REQUIRED' | 'FORMAT' | 'NOT_FOUND' | 'NO_FORECAST'; message: string };

/**
 * The check's cache key: one answer per ZIP, kept for the visit.
 *
 * @param code - The ZIP.
 */
export function postcodeCheckKey(code: string): [string, string] {
  return ['postcode-check', code.trim()];
}

/** Why a postcode was refused, as the backend names it. */
type RefusalReason = Extract<PostcodeCheck, { ok: false }>['reason'];

/**
 * A refusal in the reader's language (welcome.zip.refusal.<reason>). The
 * backend's own words are English only, so they are the fallback, for a
 * reason the dictionary does not know. Also the garden place's refusal
 * (shared/hooks/useGardenPostcode).
 *
 * @param reason - Why, as the backend said it.
 * @param zip - The postcode, for the sentences that name it.
 * @param fallback - The backend's sentence.
 */
export function postcodeRefusalWords(reason: string, zip: string, fallback: string): string {
  return t(`welcome.zip.refusal.${reason}`, { zip, defaultValue: fallback });
}

/**
 * Checks a postcode. A refusal's words come back in the reader's language.
 *
 * @param code - As typed.
 */
export async function checkPostcode(code: string): Promise<PostcodeCheck> {
  const response = await apiClient.get<PostcodeCheck & { success: boolean }>(`/postcodes/${encodeURIComponent(code.trim())}`);
  const check = response.data;
  if (!check.ok) return { ...check, message: postcodeRefusalWords(check.reason as RefusalReason, code.trim(), check.message) };
  return check;
}

/**
 * The words of a save refused for its postcode (the backend's POSTCODE_*
 * errors), in the reader's language, or null for any other failure.
 *
 * @param error - What the save threw.
 * @param zip - The postcode that was sent, for the sentences that name it.
 */
export function postcodeRefusal(error: unknown, zip = ''): string | null {
  const data = (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
  if (!data?.error?.startsWith('POSTCODE_') || !data.message) return null;
  return postcodeRefusalWords(data.error.slice('POSTCODE_'.length), zip.trim(), data.message);
}

/** What asking to be told when Grow reaches a place sends (POST /postcodes/notify). */
export interface WaitlistRequest {
  /** Where to write. */
  email: string;
  /** The postcode as typed, which Grow cannot use yet. */
  postcode: string;
  /** The browser's region ("GB" from en-GB), for the country when the postcode's shape does not say. */
  region?: string | null;
  /** The language to write in. */
  language?: string;
}

/**
 * Asks to be told when Grow reaches a place it cannot plan for yet (a postcode
 * outside the US): the backend keeps the email and the country (grow_waitlist).
 *
 * @param request - The email, the postcode and what else helps.
 * @throws When the backend refuses it (a bad email) or cannot be reached.
 */
export async function askToBeTold(request: WaitlistRequest): Promise<void> {
  await apiClient.post('/postcodes/notify', request);
}

/**
 * "Sparta, TN": the place a check found, for a line under the box.
 *
 * @param check - The check's answer, if there is one yet.
 * @returns The town and state (the ZIP when neither is known), or null for a refusal or no answer.
 */
export function placeOf(check: PostcodeCheck | null | undefined): string | null {
  if (!check?.ok) return null;
  return [check.city, check.state].filter(Boolean).join(', ') || check.zip;
}
